import { useAppStore } from '../stores/appStore';
import { useSettingsStore } from '../stores/settingsStore';
import { persistServerOrigin } from './serverOrigin';

export interface RedeemedInvite {
  gateway_url: string;
  gateway_token: string;
  display_name: string;
  allowed_agents?: string[] | null;
}

export function parseInviteLink(link: string): { inviteOrigin: string; token: string; role: string | null } {
  try {
    const withProto = link.startsWith('http') ? link : `http://${link}`;
    const url = new URL(withProto);
    const token = url.searchParams.get('token');
    if (!token) throw new Error('No token found in invite link.');
    const role = url.searchParams.get('role');
    return { inviteOrigin: url.origin, token, role };
  } catch (err) {
    if (err instanceof Error && err.message === 'No token found in invite link.') throw err;
    throw new Error('Could not parse invite link. Paste the full URL from your admin.');
  }
}

export async function redeemInviteToken(inviteOrigin: string, token: string): Promise<RedeemedInvite> {
  const res = await fetch(new URL('/api/invites/redeem', inviteOrigin).toString(), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ token }),
  });

  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error((data as { error?: string }).error ?? `Failed to redeem invite (${res.status})`);
  }

  return res.json() as Promise<RedeemedInvite>;
}

export function persistRedeemedInvite({
  token,
  inviteOrigin,
  role,
  displayName,
  allowedAgents,
  gatewayUrl,
  gatewayToken,
}: {
  token: string;
  inviteOrigin: string;
  role: string | null;
  displayName: string;
  allowedAgents?: string[] | null;
  gatewayUrl: string;
  gatewayToken: string;
}): void {
  localStorage.setItem('clawlet.inviteToken', token);
  localStorage.setItem('clawlet.fromInvite', '1');
  localStorage.setItem('clawlet.displayName', displayName);
  persistServerOrigin(inviteOrigin);

  useSettingsStore.getState().updateGeneral({ displayName });
  useAppStore.getState().setUserRole(role === 'builder' ? 'builder' : 'member');

  if (allowedAgents?.length) {
    useAppStore.getState().setAllowedAgents(allowedAgents);
  }

  useAppStore.getState().setGatewayUrl(gatewayUrl);
  useAppStore.getState().setGatewayToken(gatewayToken);
}
